/**
 * Client-side PDF text extraction via pdfjs-dist.
 * Worker is lazy-loaded only when this function is first called.
 * Cache scope: singleton per page load — worker is configured once and reused.
 */

import PdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker'

const DEFAULT_MAX_PAGES = 30
const DEFAULT_MAX_CHARS = 50000
const DEFAULT_PREVIEW_WIDTH = 420

export interface PdfExtractionResult {
  text: string
  pageCount: number
  truncated: boolean
}

let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null

export function loadPdfJs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((lib) => {
      // Use workerPort (Worker object) instead of workerSrc (URL string).
      // workerSrc causes the browser to fetch the .mjs file directly, which
      // can fail with an "application/octet-stream" MIME type error when Vite
      // serves the raw node_modules file. Passing a Worker object via
      // workerPort bypasses the MIME check entirely.
      lib.GlobalWorkerOptions.workerPort = new PdfjsWorker()
      return lib
    })
  }
  return pdfjsPromise
}

export async function extractPdfText(
  file: File,
  maxPages: number = DEFAULT_MAX_PAGES,
  maxChars: number = DEFAULT_MAX_CHARS,
): Promise<PdfExtractionResult> {
  const pdfjsLib = await loadPdfJs()
  const arrayBuffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise

  // pdf.js retains the parsed document and its transport buffers until destroyed;
  // without this `finally` a long builder session leaks memory across uploads.
  try {
    const totalPages = pdf.numPages
    const pagesToRead = Math.min(totalPages, maxPages)
    let text = ''
    let truncated = false

    for (let i = 1; i <= pagesToRead; i++) {
      const page = await pdf.getPage(i)
      const content = await page.getTextContent()
      const pageText = content.items
        .map((item) => ('str' in item ? (item as { str: string }).str : ''))
        .join(' ')
      text += pageText + '\n\n'

      if (text.length > maxChars) {
        text = text.slice(0, maxChars)
        truncated = true
        break
      }
    }

    if (pagesToRead < totalPages) {
      truncated = true
    }

    return { text: text.trim(), pageCount: totalPages, truncated }
  } finally {
    await pdf.destroy()
  }
}

export async function renderPdfFirstPagePreview(
  source: File | string,
  targetWidth: number = DEFAULT_PREVIEW_WIDTH,
): Promise<string> {
  const pdfjsLib = await loadPdfJs()
  const documentSource =
    typeof source === 'string'
      ? { url: source }
      : { data: await source.arrayBuffer() }
  const pdf = await pdfjsLib.getDocument(documentSource).promise
  try {
    const page = await pdf.getPage(1)
    const baseViewport = page.getViewport({ scale: 1 })
    // Guard against a degenerate page (width 0) producing scale = Infinity and an
    // unbounded canvas allocation (OOM risk).
    if (!baseViewport.width) {
      throw new Error('PDF page has no width; cannot render preview')
    }
    const scale = targetWidth / baseViewport.width
    const viewport = page.getViewport({ scale })
    const canvas = document.createElement('canvas')
    canvas.width = Math.ceil(viewport.width)
    canvas.height = Math.ceil(viewport.height)
    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error('Could not create PDF preview canvas')
    }
    await page.render({ canvas: null, canvasContext: context, viewport }).promise
    return canvas.toDataURL('image/png')
  } finally {
    await pdf.destroy()
  }
}

const DEFAULT_RENDER_WIDTH = 1024

// Browser canvas ceilings vary (Chrome ~16384px/dim & 268M px area; Safari far
// lower). Keep the stitched strip comfortably inside them; `compressCaptureForChat`
// downscales further to the vision budget, so this only bounds the intermediate
// canvas. If the full document is taller than this, the whole strip is scaled down
// uniformly (aspect preserved) rather than dropping pages.
const MAX_STITCH_HEIGHT = 12000

/**
 * Render EVERY page of a PDF and stitch them vertically into one full-length PNG,
 * returned as a bare base64 string (no `data:` prefix). Use when a PDF is one long
 * VISUAL creative (a landing page exported to letter sheets, an ad spread) that
 * must be seen whole — not just its first page. The caller compresses the result
 * for the vision API (e.g. `compressCaptureForChat`, which is tall-image aware).
 *
 * One PDF → one image: pages are drawn top-to-bottom onto a single canvas at a
 * shared width, over a white backdrop so transparent regions don't turn black in
 * JPEG re-encoding. A degenerate zero-width page is skipped. Throws if no page is
 * renderable.
 */
export async function renderPdfToStitchedImageBase64(
  source: File,
  targetWidth: number = DEFAULT_RENDER_WIDTH,
): Promise<string> {
  const pdfjsLib = await loadPdfJs()
  const arrayBuffer = await source.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  try {
    // Render each page to its own tile first, then measure and stitch — the total
    // height (and any uniform shrink) can only be known once every page is sized.
    const tiles: Array<HTMLCanvasElement> = []
    let totalHeight = 0
    let maxWidth = 0
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i)
      const baseViewport = page.getViewport({ scale: 1 })
      if (!baseViewport.width) {
        continue
      }
      const scale = targetWidth / baseViewport.width
      const viewport = page.getViewport({ scale })
      const tile = document.createElement('canvas')
      tile.width = Math.ceil(viewport.width)
      tile.height = Math.ceil(viewport.height)
      const tileCtx = tile.getContext('2d')
      if (!tileCtx) {
        continue
      }
      await page.render({ canvas: null, canvasContext: tileCtx, viewport }).promise
      tiles.push(tile)
      totalHeight += tile.height
      if (tile.width > maxWidth) {
        maxWidth = tile.width
      }
    }
    if (tiles.length === 0) {
      throw new Error('PDF has no renderable pages')
    }

    const shrink = totalHeight > MAX_STITCH_HEIGHT ? MAX_STITCH_HEIGHT / totalHeight : 1
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(maxWidth * shrink))
    canvas.height = Math.max(1, Math.round(totalHeight * shrink))
    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error('Could not create PDF stitch canvas')
    }
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)

    let y = 0
    for (const tile of tiles) {
      const drawWidth = Math.round(tile.width * shrink)
      const drawHeight = Math.round(tile.height * shrink)
      context.drawImage(tile, 0, y, drawWidth, drawHeight)
      y += drawHeight
    }

    return canvas.toDataURL('image/png').split(',')[1] ?? ''
  } finally {
    await pdf.destroy()
  }
}
